<template>
  <div
    ref="fabWrapper"
    class="agent-fab-wrapper"
    :style="{ right: pos.right + 'px', bottom: pos.bottom + 'px' }"
    @mousedown="onMouseDown"
    @touchstart.passive="onTouchStart"
  >
    <!-- Chat Window -->
    <AgentChat v-if="store.selectedAgent" />

    <!-- Agent Selector (circular) -->
    <transition name="fade-scale">
      <div v-if="selectorOpen && !store.selectedAgent" class="agent-selector">
        <div
          v-for="(agent, i) in store.agentes"
          :key="agent.key"
          class="agent-bubble"
          :style="bubbleStyle(i)"
          @click="pickAgent(agent)"
        >
          <div
            class="agent-bubble-inner"
            :style="{ background: agent.color }"
          >
            <span class="material-icons">{{ agent.icono }}</span>
            <span class="agent-bubble-label">{{ agent.nombre }}</span>
          </div>
          <div class="agent-bubble-hint">{{ agent.nombre }} · {{ agent.descripcion }}</div>
        </div>
      </div>
    </transition>

    <!-- FAB Button -->
    <div
      class="agent-fab"
      :class="{ 'fab-open': selectorOpen }"
      @click="toggleSelector"
      :title="selectorOpen ? 'Cerrar' : 'Asistentes IA'"
    >
      <span class="material-icons fab-icon">{{ selectorOpen ? 'close' : 'smart_toy' }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import AgentChat from './AgentChat.vue';
import { useAgentStore } from '../stores/agent.store';
import type { AgentDefinition } from '../stores/agent.store';

const store = useAgentStore();
const selectorOpen = ref(false);

// --- Drag state ---
const STORAGE_KEY = 'agent_fab_pos';
const fabWrapper = ref<HTMLElement | null>(null);
const pos = ref({ right: 28, bottom: 28 });
let dragging = false;
let dragStartX = 0;
let dragStartY = 0;
let startRight = 28;
let startBottom = 28;
let moved = false;

function loadPos() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const p = JSON.parse(saved);
      pos.value = {
        right:  Math.max(8, Math.min(p.right,  window.innerWidth  - 68)),
        bottom: Math.max(8, Math.min(p.bottom, window.innerHeight - 68)),
      };
    }
  } catch {}
}

function savePos() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value));
}

function onMouseDown(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('.agent-selector')) return;
  if ((e.target as HTMLElement).closest('.agent-chat-panel')) return;
  dragging = true;
  moved = false;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  startRight  = pos.value.right;
  startBottom = pos.value.bottom;
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup',   onMouseUp);
  e.preventDefault();
}

function onMouseMove(e: MouseEvent) {
  if (!dragging) return;
  const dx = e.clientX - dragStartX;
  const dy = e.clientY - dragStartY;
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
  pos.value = {
    right:  Math.max(8, Math.min(startRight  - dx, window.innerWidth  - 68)),
    bottom: Math.max(8, Math.min(startBottom - dy, window.innerHeight - 68)),
  };
}

function onMouseUp() {
  dragging = false;
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup',   onMouseUp);
  if (moved) savePos();
}

// Touch support
function onTouchStart(e: TouchEvent) {
  if ((e.target as HTMLElement).closest('.agent-selector')) return;
  if ((e.target as HTMLElement).closest('.agent-chat-panel')) return;
  const t = e.touches[0];
  dragging = true;
  moved = false;
  dragStartX = t.clientX;
  dragStartY = t.clientY;
  startRight  = pos.value.right;
  startBottom = pos.value.bottom;
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend',  onTouchEnd);
}

function onTouchMove(e: TouchEvent) {
  if (!dragging) return;
  e.preventDefault();
  const t = e.touches[0];
  const dx = t.clientX - dragStartX;
  const dy = t.clientY - dragStartY;
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
  pos.value = {
    right:  Math.max(8, Math.min(startRight  - dx, window.innerWidth  - 68)),
    bottom: Math.max(8, Math.min(startBottom - dy, window.innerHeight - 68)),
  };
}

function onTouchEnd() {
  dragging = false;
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend',  onTouchEnd);
  if (moved) savePos();
}

onMounted(loadPos);
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup',   onMouseUp);
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend',  onTouchEnd);
});

// --- Agent logic ---
function toggleSelector() {
  if (moved) { moved = false; return; }
  if (store.selectedAgent) { store.clearAgent(); return; }
  selectorOpen.value = !selectorOpen.value;
}

function pickAgent(agent: AgentDefinition) {
  store.selectAgent(agent);
  selectorOpen.value = false;
}

function bubbleStyle(index: number) {
  const total = store.agentes.length;
  const radius = 120;
  const startAngle = 210;
  const endAngle   = 330;
  const step  = total > 1 ? (endAngle - startAngle) / (total - 1) : 0;
  const angle = (startAngle + index * step) * (Math.PI / 180);
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;
  return {
    transform: `translate(${x}px, ${y}px)`,
    transitionDelay: `${index * 0.05}s`,
  };
}
</script>

<style scoped>
.agent-fab-wrapper {
  position: fixed;
  z-index: 9999;
  cursor: grab;
}
.agent-fab-wrapper:active {
  cursor: grabbing;
}

.agent-fab {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #11105E, #FE9000);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  user-select: none;
}

.agent-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
}

.agent-fab.fab-open {
  background: linear-gradient(135deg, #c62828, #e53935);
}

.fab-icon {
  color: white;
  font-size: 30px;
}

/* Selector container */
.agent-selector {
  position: absolute;
  bottom: 30px;
  right: 30px;
  width: 0;
  height: 0;
}

/* Each bubble */
.agent-bubble {
  position: absolute;
  bottom: 0;
  right: 0;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1;
}

.agent-bubble:hover {
  z-index: 50;
}

.agent-bubble-inner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease;
  transform: translate(-50%, -50%);
}

.agent-bubble-inner:hover {
  transform: translate(-50%, -50%) scale(1.15);
}

.agent-bubble-inner .material-icons {
  color: white;
  font-size: 22px;
}

.agent-bubble-label {
  color: white;
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-top: 1px;
  text-transform: uppercase;
}

/* Hint label that appears to the left on hover */
.agent-bubble-hint {
  position: absolute;
  right: 36px;   /* 28px (half bubble) + 8px gap */
  top: 0;
  transform: translateY(-50%);
  background: rgba(17, 16, 94, 0.88);
  color: white;
  padding: 5px 12px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.agent-bubble:hover .agent-bubble-hint {
  opacity: 1;
}

/* Transition */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: opacity 0.2s ease;
}
.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
}
</style>
